import { buildLibraryScripts } from './htmlJsLibraries';

// Shared sandbox primitives for rendering UNTRUSTED author HTML/JS in an opaque-origin iframe.
// Two consumers:
//   - HtmlJs4.jsx — the promoted v4 "htmljs" report component. Uses buildSrcDoc(), which also
//     injects the window.Reservoir postMessage bootstrap (live data / analyze / requestState).
//   - src/ai/ArtifactFrame.jsx — the in-chat Catalyst *mockup* artifact. Uses buildArtifactSrcDoc(),
//     which injects the same CSP + ReservoirKit but NO window.Reservoir bootstrap: a mockup renders
//     baked-in static data, and the absence of window.Reservoir is exactly what the author's
//     promote-time swap keys off (static call → Reservoir.onData(render)).
// Both must use the SAME CSP + opaque sandbox so what renders in the mockup renders identically once
// promoted. See ai/requests/freeform_report/.

// Content-Security-Policy applied to every sandboxed document. With sandbox="allow-scripts" (and
// never allow-same-origin) this contains the untrusted code the same way Catalyst's ArtifactFrame
// does:
//  - default-src 'none'          → nothing loads/connects unless re-allowed
//  - script-src/style-src inline → the component's own inline JS/CSS runs (srcdoc = opaque origin)
//  - img-src data: blob:         → only self-contained (embedded) images; external <img> is an
//                                  exfil channel (fires even with connect-src 'none') so it is
//                                  NOT allowed.
//  - connect-src 'none'          → blocks fetch / XHR / WebSocket / sendBeacon; no network at all.
//  - form-action / base-uri none → blocks form-POST exfiltration and <base> hijack.
export const HTMLJS_CSP =
	"default-src 'none'; " +
	"script-src 'unsafe-inline'; " +
	"style-src 'unsafe-inline'; " +
	"img-src data: blob:; " +
	"font-src data:; " +
	"media-src data: blob:; " +
	"connect-src 'none'; " +
	"form-action 'none'; " +
	"base-uri 'none'";

// Trusted bootstrap injected as the first thing in <head> so window.Reservoir exists before any
// author script runs. The nonce — not the origin — authenticates the channel: a srcdoc frame is an
// opaque origin ("null"), so we validate event.source === window.parent and a matching nonce, and
// the host validates event.source === iframe.contentWindow and the same nonce on the way back.
export function buildBootstrap(nonce) {
	// JSON.stringify does not escape "/", so a "</script>" in the value would close this inline
	// script in the HTML parser. The nonce is always our generated base36 (never user input), but
	// escape "<" anyway so the serialized literal can never break out of the <script> tag.
	const n = JSON.stringify(String(nonce)).replace(/</g, '\\u003c');
	return (
		'<script>(function(){' +
		'var N=' + n + ';' +
		'var cb=null,pending=null,got=false,aseq=0,apend={};' +
		'window.addEventListener("message",function(e){' +
			'if(e.source!==window.parent)return;' +
			'var m=e.data;if(!m||m.nonce!==N)return;' +
			'if(m.kind==="init"||m.kind==="data"){got=true;if(cb){cb(m.data);}else{pending=m.data;}}' +
			'else if(m.kind==="analyze-response"){var f=apend[m.reqId];if(f){delete apend[m.reqId];f({ok:!!m.ok,ai:m.ai||null,error:m.error||null});}}' +
		'});' +
		'window.Reservoir={' +
			'onData:function(fn){cb=fn;if(got){var d=pending;pending=null;fn(d);}},' +
			// analyze asks the host to run one of the component's ADMIN-APPROVED analyses (by key)
			// over its current rows and calls back with {ok, ai:{finalText,displayParts}, error}. The
			// frame cannot author the prompt — only trigger a key the admin configured. The host
			// enforces the AI gate, debounces/single-flights, and grounds the model on scoped rows.
			'analyze:function(opts,fn){var id=(++aseq);if(typeof fn==="function")apend[id]=fn;' +
				'window.parent.postMessage({nonce:N,kind:"analyze-request",reqId:id,promptKey:(opts&&opts.promptKey)||""},"*");},' +
			// requestState asks the host to re-fetch THIS component with new page/sort/groupingLayout
			// (e.g. a metric switch that needs fresh server aggregation, a drill, or paging). The host
			// re-enforces the v4 contract (report scope, hideFromAI, filters, field allow-list) and the
			// refreshed rows arrive back through the SAME onData callback — onData is the one data sink.
			'requestState:function(s){window.parent.postMessage({nonce:N,kind:"request-state",state:s},"*");},' +
			'resize:function(h){window.parent.postMessage({nonce:N,kind:"resize",height:h},"*");},' +
			'ready:function(){window.parent.postMessage({nonce:N,kind:"ready"},"*");}' +
		'};' +
		// Auto-announce readiness once the document is parsed, so author code that just calls
		// Reservoir.onData(fn) (without calling ready()) still receives the init push.
		'if(document.readyState==="loading"){document.addEventListener("DOMContentLoaded",window.Reservoir.ready);}else{window.Reservoir.ready();}' +
		'})();</script>'
	);
}

// Inject a trusted <head> prefix as the first children of <head> so the CSP governs the whole
// document and the injected scripts run before author scripts. Mirrors ArtifactFrame's injectCsp
// fallbacks for documents that omit <head>/<html>/doctype.
function injectHead(authorHtml, head) {
	const html = typeof authorHtml === 'string' ? authorHtml : '';
	if (/<head[^>]*>/i.test(html)) {
		return html.replace(/<head[^>]*>/i, (m) => `${m}${head}`);
	}
	if (/<html[^>]*>/i.test(html)) {
		return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${head}</head>`);
	}
	if (/^\s*<!doctype[^>]*>/i.test(html)) {
		return html.replace(/^\s*<!doctype[^>]*>/i, (m) => `${m}<head>${head}</head>`);
	}
	return `<!doctype html><html><head>${head}</head><body>${html}</body></html>`;
}

// Build the srcdoc for a PROMOTED htmljs component. Head order: CSP meta first (so it governs
// everything), then the trusted bootstrap (window.Reservoir), then any admin-approved host-injected
// libraries, then the author document.
export function buildSrcDoc(authorHtml, nonce, libraries) {
	const head = `<meta http-equiv="Content-Security-Policy" content="${HTMLJS_CSP}">`
		+ buildBootstrap(nonce)
		+ buildLibraryScripts(libraries);
	return injectHead(authorHtml, head);
}

// Build the srcdoc for an in-chat MOCKUP artifact. Same CSP + opaque-origin sandbox as the promoted
// component and the same ReservoirKit helpers, but NO window.Reservoir bootstrap — a mockup renders
// baked-in static data. `includeKit` injects the reservoir-kit bundle (default on) so a mockup can
// use the exact ReservoirKit.renderTable/renderMarkdown helpers the live component uses.
export function buildArtifactSrcDoc(content, { includeKit = true } = {}) {
	const head = `<meta http-equiv="Content-Security-Policy" content="${HTMLJS_CSP}">`
		+ (includeKit ? buildLibraryScripts(['reservoir-kit']) : '');
	return injectHead(content, head);
}

export function makeNonce() {
	try {
		const a = new Uint32Array(4);
		(window.crypto || window.msCrypto).getRandomValues(a);
		return Array.from(a, (n) => n.toString(36)).join('');
	} catch (e) {
		return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
	}
}

// Height clamps shared by the sandboxed renderers.
export const MIN_HEIGHT = 120;
export const MAX_HEIGHT = 4000;

// Keys an htmljs component is allowed to change via requestState. Report-level filters are global
// (they affect other components), so they are intentionally NOT here — a component scopes itself
// through its own page/sort/grouping only. The server re-validates every name against the stored
// definition regardless, so this is a first guard, not the only one.
export const ALLOWED_STATE_KEYS = ['page', 'sort', 'groupingLayout', 'hiddenColumns', 'columnAggregates'];

export function pickAllowedState(partial) {
	if (!partial || typeof partial !== 'object') return null;
	const out = {};
	for (const k of ALLOWED_STATE_KEYS) {
		if (Object.prototype.hasOwnProperty.call(partial, k)) out[k] = partial[k];
	}
	return Object.keys(out).length ? out : null;
}
